import React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, BarChart3, LinkIcon, QrCode, Sparkles, Target, Zap, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { apiAxios } from '@/lib/apiAxios';
import { useQueryClient } from '@tanstack/react-query';
export const Route = createFileRoute('/console/_authenticated/_layout/onboarding/')({
    component: OnboardingPage,
});
const TOTAL_STEPS = 4;
const slideVariants = {
    enter: (direction) => ({
        x: direction > 0 ? 300 : -300,
        opacity: 0,
        scale: 0.95,
    }),
    center: {
        x: 0,
        opacity: 1,
        scale: 1,
        transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
    },
    exit: (direction) => ({
        x: direction > 0 ? -300 : 300,
        opacity: 0,
        scale: 0.95,
        transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] },
    }),
};
const staggerChildren = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.12, delayChildren: 0.2 } },
};
const fadeUp = {
    hidden: { opacity: 0, y: 24 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};
const scaleIn = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};
function GlassCard({ children, className }) {
    return (<div className={cn('relative rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur-xl', 'shadow-[0_8px_32px_rgba(0,0,0,0.08)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.3)]', 'dark:border-white/[0.06] dark:bg-white/[0.03]', className)}>
            {children}
        </div>);
}
function StepIndicator({ current, total }) {
    return (<div className="flex items-center gap-2">
            {Array.from({ length: total }, (_, i) => (<motion.div key={i} className={cn('h-1.5 rounded-full transition-all duration-500', i === current ? 'w-8 bg-primary' : i < current ? 'w-4 bg-primary/40' : 'w-4 bg-muted')} layout/>))}
        </div>);
}
function WelcomeStep() {
    return (<motion.div className="flex flex-col items-center text-center" variants={staggerChildren} initial="hidden" animate="visible">
            <motion.div variants={scaleIn} className="relative mb-8">
                <div className="absolute inset-0 animate-pulse rounded-full bg-primary/20 blur-2xl"/>
                <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary/60 shadow-lg shadow-primary/25">
                    <Sparkles className="h-10 w-10 text-primary-foreground"/>
                </div>
            </motion.div>

            <motion.h1 variants={fadeUp} className="text-4xl font-bold tracking-tight md:text-5xl">
                Welcome to{' '}
                <span className="bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">Adora</span>
            </motion.h1>

            <motion.p variants={fadeUp} className="mt-4 max-w-lg text-base text-muted-foreground leading-relaxed md:text-lg">
                Turn every QR scan, flyer, and tuk-tuk ad into measurable data. Let&apos;s set up your first campaign in
                under 2 minutes.
            </motion.p>

            <motion.div variants={fadeUp} className="mt-8 flex flex-wrap justify-center gap-4">
                {[
            { icon: Target, label: 'Track intent' },
            { icon: BarChart3, label: 'Real-time analytics' },
            { icon: Zap, label: 'Instant setup' },
        ].map(({ icon: Icon, label }) => (<GlassCard key={label} className="flex items-center gap-3 px-4 py-3">
                        <Icon className="h-4 w-4 text-primary"/>
                        <span className="text-sm font-medium">{label}</span>
                    </GlassCard>))}
            </motion.div>
        </motion.div>);
}
function HowItWorksStep() {
    const steps = [
        {
            icon: QrCode,
            title: 'Create a Campaign',
            description: 'Upload your artwork, name your campaign, and get a unique QR code and short link instantly.',
            gradient: 'from-violet-500/20 to-violet-500/5',
            iconBg: 'bg-violet-500/10 text-violet-600 dark:text-violet-400',
        },
        {
            icon: LinkIcon,
            title: 'Add Tracking Links',
            description: 'Create destinations for each channel — Facebook, TikTok, your website, physical stores, and more.',
            gradient: 'from-blue-500/20 to-blue-500/5',
            iconBg: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
        },
        {
            icon: BarChart3,
            title: 'View Analytics',
            description: 'Every scan is recorded in real time — device, browser, location, intent. See what truly works.',
            gradient: 'from-emerald-500/20 to-emerald-500/5',
            iconBg: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
        },
    ];
    return (<motion.div variants={staggerChildren} initial="hidden" animate="visible">
            <motion.div variants={fadeUp} className="mb-8 text-center">
                <h2 className="text-3xl font-bold tracking-tight">How It Works</h2>
                <p className="mt-2 text-muted-foreground">Three simple steps to measurable marketing</p>
            </motion.div>

            <div className="grid gap-5 md:grid-cols-3">
                {steps.map((step, i) => (<motion.div key={step.title} variants={fadeUp} custom={i}>
                        <GlassCard className="group h-full transition-all duration-300 hover:scale-[1.02] hover:shadow-lg">
                            <div className={cn('mb-4 inline-flex rounded-xl p-3', step.iconBg)}>
                                <step.icon className="h-6 w-6"/>
                            </div>
                            <div className="mb-1 flex items-baseline gap-2">
                                <span className="text-xs font-bold text-primary">0{i + 1}</span>
                                <h3 className="text-lg font-semibold">{step.title}</h3>
                            </div>
                            <p className="text-sm leading-relaxed text-muted-foreground">{step.description}</p>
                        </GlassCard>
                    </motion.div>))}
            </div>
        </motion.div>);
}
function WhyAdoraStep() {
    const benefits = [
        'Know which flyer, poster, or ad actually drives visits',
        'Compare channels side-by-side with A/B testing',
        'See scans by device, browser, and location in real time',
        'Stop guessing — invest only in what works',
    ];
    return (<motion.div variants={staggerChildren} initial="hidden" animate="visible">
            <motion.div variants={fadeUp} className="mb-8 text-center">
                <h2 className="text-3xl font-bold tracking-tight">Why Adora?</h2>
                <p className="mt-2 text-muted-foreground">Every campaign tells a story. Adora helps you read it.</p>
            </motion.div>

            <GlassCard className="mx-auto max-w-xl">
                <ul className="space-y-4">
                    {benefits.map((benefit, i) => (<motion.li key={i} variants={fadeUp} custom={i} className="flex items-start gap-3">
                            <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10">
                                <Check className="h-3 w-3 text-primary"/>
                            </div>
                            <span className="text-sm leading-relaxed">{benefit}</span>
                        </motion.li>))}
                </ul>
            </GlassCard>

            <motion.div variants={fadeUp} className="mt-6 text-center">
                <GlassCard className="mx-auto inline-block px-6 py-3">
                    <p className="text-xs text-muted-foreground">
                        <Sparkles className="mr-1.5 inline h-3.5 w-3.5 text-primary"/>
                        Less than one day of tuk-tuk advertising per month.
                    </p>
                </GlassCard>
            </motion.div>
        </motion.div>);
}
function ReadyStep() {
    return (<motion.div className="flex flex-col items-center text-center" variants={staggerChildren} initial="hidden" animate="visible">
            <motion.div variants={scaleIn} className="relative mb-8">
                <div className="absolute inset-0 rounded-full bg-emerald-500/20 blur-2xl"/>
                <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 shadow-lg shadow-emerald-500/25">
                    <Check className="h-10 w-10 text-white"/>
                </div>
            </motion.div>

            <motion.h2 variants={fadeUp} className="text-3xl font-bold tracking-tight md:text-4xl">
                You&apos;re all set!
            </motion.h2>

            <motion.p variants={fadeUp} className="mt-4 max-w-md text-base text-muted-foreground leading-relaxed">
                Create your first campaign and start tracking real engagement from day one. It only takes a minute.
            </motion.p>
        </motion.div>);
}
function OnboardingPage() {
    const navigate = Route.useNavigate();
    const queryClient = useQueryClient();
    const [step, setStep] = React.useState(0);
    const [direction, setDirection] = React.useState(1);
    const [completing, setCompleting] = React.useState(false);
    const next = React.useCallback(() => {
        if (step < TOTAL_STEPS - 1) {
            setDirection(1);
            setStep((s) => s + 1);
        }
    }, [step]);
    const prev = React.useCallback(() => {
        if (step > 0) {
            setDirection(-1);
            setStep((s) => s - 1);
        }
    }, [step]);
    const complete = React.useCallback(async () => {
        setCompleting(true);
        try {
            await apiAxios.patch('/users/me/onboarding');
            await queryClient.invalidateQueries({ queryKey: ['me'] });
            navigate({ to: '/console/campaigns/new' });
        }
        catch {
            navigate({ to: '/console' });
        }
    }, [navigate, queryClient]);
    const stepComponents = [WelcomeStep, HowItWorksStep, WhyAdoraStep, ReadyStep];
    const StepComponent = stepComponents[step];
    return (<div className="relative min-h-[calc(100vh-8rem)] flex flex-col items-center justify-center overflow-hidden px-4 py-10">
            {/* Background blurs */}
            <div className="pointer-events-none absolute -top-40 left-1/4 h-96 w-96 rounded-full bg-primary/8 blur-3xl"/>
            <div className="pointer-events-none absolute -bottom-40 right-1/4 h-96 w-96 rounded-full bg-violet-500/8 blur-3xl"/>
            <div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-64 w-64 rounded-full bg-emerald-500/5 blur-3xl"/>

            {/* Step indicator */}
            <div className="mb-10">
                <StepIndicator current={step} total={TOTAL_STEPS}/>
            </div>

            {/* Step content */}
            <div className="relative w-full max-w-3xl" style={{ minHeight: 380 }}>
                <AnimatePresence mode="wait" custom={direction}>
                    <motion.div key={step} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className="w-full">
                        <StepComponent />
                    </motion.div>
                </AnimatePresence>
            </div>

            {/* Navigation */}
            <motion.div className="mt-10 flex items-center gap-3" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.4 }}>
                {step > 0 && (<Button variant="ghost" size="lg" onClick={prev} className="gap-2">
                        <ArrowLeft className="h-4 w-4"/>
                        Back
                    </Button>)}

                {step < TOTAL_STEPS - 1 ? (<Button size="lg" onClick={next} className="gap-2 px-8">
                        Continue
                        <ArrowRight className="h-4 w-4"/>
                    </Button>) : (<Button size="lg" onClick={complete} disabled={completing} className="gap-2 bg-gradient-to-r from-primary to-primary/80 px-8 shadow-lg shadow-primary/25 transition-all hover:shadow-xl hover:shadow-primary/30">
                        {completing ? 'Setting up...' : 'Create Your First Campaign'}
                        <ArrowRight className="h-4 w-4"/>
                    </Button>)}
            </motion.div>

            {/* Skip */}
            {step < TOTAL_STEPS - 1 && (<motion.button onClick={complete} className="mt-4 text-xs text-muted-foreground hover:text-foreground transition-colors" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}>
                    Skip for now
                </motion.button>)}
        </div>);
}
